import React, { FC, ReactNode, useEffect } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { FormDatePicker } from '../../../../../components/date-picker/index';
import { FormInput } from '../../../../../components/input/index';
import { SafeHooks, ScenarioHooks, useFormat } from '../../../../../hooks/index';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { Snapshot, SnapshotListItem } from '../../../../../types/snapshot.types';
import { toNumber } from '../../../../../utils/to-number.util';
import { useScenarioStore } from '../../../scenario-modeling.store';
import { FundraisingFormSchema } from '../../fundraising-validation';
import { ScenarioDropdown } from '../scenario-dropdown/index';
import {
  CommonGrantItemList,
  CustomSafeItems,
  InvestmentItemList,
  PoolItemList,
  SafeItemList,
} from '../scenario-dropdown/items/index';
import {
  ExistingScenarioDetails,
  fundraisingScenarioItems,
  FundraisingScenarioKey,
  NewScenarioDetails,
} from './fundraising.types';
import { useFundraisingCreation } from './use-fundraising-creation';

type FundraisingScenarioFormProps = {
  addButtonText: ReactNode | string;
  additionalColumns: (ReactNode | string)[];
  additionalColumnsCount: number;
  columns: (ReactNode | string)[];
  columnsCount: number;
  companyId: string;
  control: Control<FundraisingFormSchema>;
  scenarioId: string;
  scenarioType: ScenarioType;
  setValue: UseFormSetValue<FundraisingFormSchema>;
};

const scenarioFormMap: Record<
  FundraisingScenarioKey,
  (props: FundraisingScenarioFormProps) => ReactNode
> = {
  [FundraisingScenarioKey.POOLS]: (props) => <PoolItemList {...props} />,
  [FundraisingScenarioKey.CUSTOM_SAFES]: (props) => <CustomSafeItems {...props} />,
  [FundraisingScenarioKey.INVESTMENT]: (props) => <InvestmentItemList {...props} />,
  [FundraisingScenarioKey.SAFES]: (props) => <SafeItemList {...props} />,
  [FundraisingScenarioKey.GRANTS]: (props) => <CommonGrantItemList {...props} />,
};

export type FundraisingCreationProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  scenarioId: string;
  scenarioType: ScenarioType;
  setValue: UseFormSetValue<FundraisingFormSchema>;
  snapshot?: Snapshot | SnapshotListItem;
};

export const FundraisingCreation: FC<FundraisingCreationProps> = ({
  snapshot,
  control,
  companyId,
  scenarioId,
  setValue,
  scenarioType,
}) => {
  const { format } = useFormat();
  const { safes, totalSafesCount } = SafeHooks.useSafes({
    companyId,
  });
  const { scenarioVestingTasks } = ScenarioHooks.useScenarioVestingTasks({ scenarioId });
  // const { scenarioVestingSars } = ScenarioHooks.useScenarioVestingSars({
  //   scenarioId,
  // });

  const { pointerBlockedStyles } = useScenarioStore();

  const { scenario } = ScenarioHooks.useScenario({ scenarioId });

  const {
    customSafes,
    pools,
    investments,
    safes: selectedSafes,
    grants,
  } = useWatch<FundraisingFormSchema>({ control });
  const valuation = useWatch({ control, name: 'valuation' });

  const { debounceValuationCallback, isPending } = useFundraisingCreation({
    scenarioId,
    valuation,
  });

  useEffect(() => {
    debounceValuationCallback(valuation);
  }, [debounceValuationCallback, valuation]);

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div className="flex items-center justify-between gap-4 rounded-lg p-5 shadow-sm">
        <span className="text-base font-semibold text-gray-700">
          <AppFormattedMessage id="ROUND_DETAILS" />
        </span>
        <div className={twMerge('flex items-center gap-4')}>
          <FormInput
            control={control}
            defaultValue={
              snapshot?.valuation?.issuedSharesOnStart && snapshot?.valuation?.sharePrice
                ? snapshot?.valuation?.issuedSharesOnStart * snapshot?.valuation?.sharePrice
                : undefined
            }
            disabled={isPending}
            disabledInput={Boolean(pointerBlockedStyles)}
            isShownErrorMessage={false}
            name={'valuation.preMoneyValuation'}
            placeholder={<AppFormattedMessage id="VALUATION" />}
            shouldFormatNumber
            wrapperClassName={twMerge('min-w-[140px] max-w-[229px]', pointerBlockedStyles)}
          />
          <FormDatePicker
            calendar={{
              fromDate: scenario?.currentValuation ? new Date() : snapshot?.valuation?.date,
            }}
            disabled={isPending || Boolean(pointerBlockedStyles)}
            iconColor="#344054"
            inputValue={valuation?.date ? format(valuation?.date, 'dd/MM/yyyy') : ''}
            onSelect={(date) => {
              setValue('valuation.date', date);
            }}
            placeholder={<AppFormattedMessage id="CLOSING_DATE" />}
            value={valuation?.date && new Date(valuation?.date)}
            wrapperClassName={twMerge('max-w-[229px]', pointerBlockedStyles)}
          />
        </div>
      </div>
      {fundraisingScenarioItems.map((item) => {
        const { description, key, hoverText, title, showButton, columns, columnsCount } = item;

        const itemsMap: Record<
          FundraisingScenarioKey,
          { itemsCount: number; selectedCount: null | number }
        > = {
          [FundraisingScenarioKey.POOLS]: {
            itemsCount: pools?.poolItems ? pools?.poolItems.length : 0,
            selectedCount: null,
          },
          [FundraisingScenarioKey.CUSTOM_SAFES]: {
            itemsCount: customSafes?.safeItems ? customSafes?.safeItems.length : 0,
            selectedCount: null,
          },
          // [FundraisingScenarioKey.GRANTS]: {
          //   itemsCount: [...(scenarioVestingTasks || []), ...(scenarioVestingSars || [])]?.length,
          //   selectedCount: (grants?.grantItems?.length || 0) + (sars?.sarItems?.length || 0),
          // },
          [FundraisingScenarioKey.GRANTS]: {
            itemsCount: (scenarioVestingTasks || [])?.length,
            selectedCount: grants?.grantItems?.length || 0,
          },
          [FundraisingScenarioKey.INVESTMENT]: {
            itemsCount: investments?.investmentItems ? investments?.investmentItems?.length : 0,
            selectedCount: null,
          },
          [FundraisingScenarioKey.SAFES]: {
            itemsCount: toNumber(totalSafesCount),
            selectedCount: Object.keys(selectedSafes?.safeItems ?? {}).length,
          },
        };

        return (
          <ScenarioDropdown
            companyId={companyId}
            control={control}
            description={description}
            hoverText={hoverText}
            isSelectChecked={
              safes &&
              selectedSafes &&
              safes.length === Object.keys(selectedSafes?.safeItems ?? {}).length
            }
            itemsCount={itemsMap[key].itemsCount}
            key={key}
            scenarioId={scenarioId}
            selectedCount={itemsMap[key].selectedCount}
            showButton={showButton && itemsMap[key].itemsCount > 0}
            title={title}
            type={key}
          >
            {scenarioFormMap[key]({
              columns,
              columnsCount,
              companyId,
              control,
              scenarioId,
              setValue,
              additionalColumns: (item as ExistingScenarioDetails).additionalColumns || [],
              additionalColumnsCount: (item as ExistingScenarioDetails).additionalColumnsCount || 0,
              addButtonText: (item as NewScenarioDetails).addButtonText || '',
              scenarioType,
            })}
          </ScenarioDropdown>
        );
      })}
    </div>
  );
};
